import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, IconButton, Typography } from '@mui/material';
import EntityFormModal from '@sharedCR/common/EntityFormModal';
import { CancelIcon } from '@sharedCR/coreDesignSystem/icons';
import { ModalSize } from '@sharedCR/coreDesignSystem/Modals';
import DocumentView from 'app/shared/components/document/DocumentView';
import PropTypes from 'prop-types';

import { AiFormFieldProvenanceSource } from 'constants/aiProcessRuns';
import { IconSize } from 'constants/coreDesignSystem';

import { selectIsFormActionInFlight } from 'store/aiProcessRuns';
import { openDialogAction } from 'store/global/actions';

import { FORM_REVIEW_LABELS, FormReviewTestIds } from '../FormReviewCard.constants';

import { surfaceDrawerStyles, surfaceTitleStyles } from './FormReviewSurface.styles';
import FormReviewSurfaceContent from './FormReviewSurfaceContent';
import FormReviewSurfaceControls from './FormReviewSurfaceControls';

const documentViewClasses = {
  documentViewWrapper: {
    maxHeight: '80vh',
  },
};

const FormReviewSurface = ({ form, isOpen, onClose }) => {
  const dispatch = useDispatch();
  const isInFlight = useSelector(selectIsFormActionInFlight(form?.id));

  const onOpenDocument = useCallback(
    field => {
      if (
        field?.provenanceSource !== AiFormFieldProvenanceSource.AI_EXTRACTED ||
        !field?.provenanceDocumentId
      ) {
        return;
      }

      dispatch(
        openDialogAction({
          header: 'View source document',
          size: ModalSize.XL,
          hasScrollY: false,
          body: () => (
            <DocumentView
              isDownloadable
              isView
              classes={documentViewClasses}
              docId={field.provenanceDocumentId}
              initialSelectedPage={field.provenancePage ?? null}
              isControlVisible={false}
            />
          ),
        }),
      );
    },
    [dispatch],
  );

  const additionalProps = useMemo(
    () => ({ form, isInFlight, onOpenDocument }),
    [form, isInFlight, onOpenDocument],
  );

  const customControls = useMemo(
    () => (form ? <FormReviewSurfaceControls form={form} /> : null),
    [form],
  );

  // EntityFormModal only renders its built-in close (✕) inside the head, which
  // mounts only with headTabs — this surface has none, and customControls
  // replaces the default Cancel button. Supply a persistent close affordance via
  // the title node so it survives every form/transition state.
  const titleNode = useMemo(
    () => (
      <Box sx={surfaceTitleStyles}>
        <Typography variant="bold32">{FORM_REVIEW_LABELS.SURFACE_TITLE}</Typography>
        <IconButton
          disableRipple
          aria-label={FORM_REVIEW_LABELS.SURFACE_CLOSE}
          color="text.dark"
          data-testid={FormReviewTestIds.SURFACE_CLOSE_BUTTON}
          onClick={onClose}
        >
          <CancelIcon size={IconSize[20]} />
        </IconButton>
      </Box>
    ),
    [onClose],
  );

  return (
    <EntityFormModal
      isData
      isHiddenNavigationTabs
      withOuterClickClose
      Content={FormReviewSurfaceContent}
      additionalProps={additionalProps}
      customControls={customControls}
      customStyles={surfaceDrawerStyles}
      isOpen={isOpen}
      title={titleNode}
      onClose={onClose}
    />
  );
};

FormReviewSurface.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  form: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
  }),
};

export default memo(FormReviewSurface);
